import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { cloneDeep, isNumber, isUndefined } from "lodash";

import { Order } from "../../../../../../services";
import {
	useTypedDispatch,
	useTypedSelector,
} from "../../../../../../redux/store";
import orderPage from "../../../../../../redux/reducers/OrdersPage";
import { formatOrder } from "../../../../../../redux/constants/OrdersPage/order";
import { useAppContext } from "../../../../../../context";
import { RoundingMethod } from "../../../../../../pages/Settings/pages/Tariffs/tabs/Additional/components/Modal/components/Content/tabs/Main/components/Rounding/constants";
import { ConstantFormat } from "../../../../../../utils/DateFns";

interface Value {
	action: "add" | "set";
	value: number;
}

function formatDuration(elapsedMinutes: number): string {
	// eslint-disable-next-line no-param-reassign
	elapsedMinutes = Math.floor(Math.abs(elapsedMinutes));

	const minutes = Math.floor(elapsedMinutes % 60);
	const hours = Math.floor(elapsedMinutes / 60);

	const formattedHours = String(hours).padStart(2, "0");
	const formattedMinutes = String(minutes).padStart(2, "0");

	return `${formattedHours}:${formattedMinutes}`;
}

function useBottomData(activeOrder: any) {
	const { t } = useTranslation();
	const dispatch = useTypedDispatch();
	const { dateFns, currencyGlobalSettings } = useAppContext();

	// The price/cost server-side recalculation (rounding, discounts, etc.)
	// can't be replicated on the client, so refetch and replace `activeOrder`
	// with the server's fresh state instead of guessing the new price locally.
	const refreshActiveOrder = useCallback(
		async (orderId: number) => {
			const refreshed = await Order.getById(orderId, {
				disableSanitize: false,
			});
			if (refreshed) {
				dispatch(
					orderPage.actions.setActiveOrder(formatOrder(refreshed)),
				);
			}
		},
		[dispatch],
	);

	const additionalTariffSettings = useTypedSelector((state) =>
		state.additionalTariffs.models.at(0),
	);

	/**
	 * Why these calculations:
	 *
	 * Server (`OrderRoute` + cost calc) has two suburban sources:
	 * 1) Geometry / price zones → base «за городом» (route.suburbanPriceZones, km).
	 * 2) Manual override `suburbanDistanceOverride { value, shrink }`.
	 *
	 * `value` on the server is always an **absolute target** suburban km
	 * (not «how much more to add» by itself):
	 * - shrink=true  (UI action "set") → replace/redistribute suburban to `value`
	 * - shrink=false (UI action "add") → bring suburban to `value` by
	 *   growing/trimming suburban segments (delta vs geometry; +/- allowed).
	 *
	 * After cost recalc, `cost.distances.suburban` is meters and already
	 * reflects the applied override — prefer it for display / next "add" base.
	 * Form default `{ value: 0, shrink: false }` is a dummy, not a real override.
	 */
	const getOutOfTownBaseKm = useCallback((order: typeof activeOrder) => {
		const distancesSuburbanM = order?.cost?.distances?.suburban;
		const distancesSuburbanKm = isNumber(distancesSuburbanM)
			? distancesSuburbanM / 1000
			: undefined;
		const routeSuburbanKm = isNumber(order?.route?.suburbanPriceZones)
			? order.route.suburbanPriceZones
			: undefined;
		const overrideValue =
			order?.additionalFields?.suburbanDistanceOverride?.value;
		const overrideShrink =
			order?.additionalFields?.suburbanDistanceOverride?.shrink;
		const isDummyOverride = overrideValue === 0 && overrideShrink === false;

		// Priority: applied distances → real override target → zone geometry.
		if (isNumber(distancesSuburbanKm)) return distancesSuburbanKm;
		if (!isDummyOverride && isNumber(overrideValue)) return overrideValue;
		if (isNumber(routeSuburbanKm)) return routeSuburbanKm;
		return undefined;
	}, []);

	const outOfTownDistanceValue = useMemo<Value | undefined>(() => {
		// --- old ---
		// const suburbanDistance = activeOrder?.route?.suburbanPriceZones as
		// 	| number
		// 	| undefined;
		// const suburbanDistanceOverride = activeOrder?.cost?.distances?.suburban;
		// if (
		// 	isUndefined(suburbanDistance) &&
		// 	isUndefined(suburbanDistanceOverride)
		// ) {
		// 	return undefined;
		// }
		// const totalSuburbanDistance =
		// 	suburbanDistanceOverride / 1000 || suburbanDistance || 0;
		// return {
		// 	action: activeOrder.additionalFields?.suburbanDistanceOverride
		// 		?.shrink
		// 		? "set"
		// 		: "add",
		// 	value: totalSuburbanDistance,
		// };

		// Show current effective suburban km; map shrink → UI action mode.
		const effectiveKm = getOutOfTownBaseKm(activeOrder);
		if (isUndefined(effectiveKm)) return undefined;

		const shrink =
			activeOrder?.additionalFields?.suburbanDistanceOverride?.shrink ===
			true;

		return {
			action: shrink ? "set" : "add",
			value: Number(Number(Math.max(effectiveKm, 0)).toFixed(3)),
		};
	}, [activeOrder, getOutOfTownBaseKm]);

	const updateOutOfTownDistance = useCallback(
		(newOutOfTownDistance: Value) => {
			if (!("id" in activeOrder)) return;

			// --- old ---
			// let newValue = cloneDeep(
			// 	activeOrder.additionalFields.suburbanDistanceOverride.value,
			// );
			// const suburbanDistance = (activeOrder?.route?.suburbanPriceZones ??
			// 	0) as number;
			// const suburban = ((activeOrder?.cost?.distances?.suburban ?? 0) /
			// 	1000) as number;
			// const payload = suburban ?? suburbanDistance ?? 0;
			// const shrink = newOutOfTownDistance.action === "set";
			// if (newOutOfTownDistance.action === "set") {
			// 	newValue = Math.max(newOutOfTownDistance.value, 0);
			// } else {
			// 	const value = Number(Number(payload).toFixed(3));
			// 	newValue = value + newOutOfTownDistance.value;
			// }

			// Convert UI set/add into absolute target km for the server.
			// Example: effective 7.7, add -5.7 → send value=2, shrink=false.
			const baseKm = getOutOfTownBaseKm(activeOrder) ?? 0;
			const shrink = newOutOfTownDistance.action === "set";
			const newValue = shrink
				? Math.max(newOutOfTownDistance.value, 0)
				: Math.max(baseKm + newOutOfTownDistance.value, 0);

			Order.updateAdditionalFieldsByOrderId({
				orderId: activeOrder.id,
				value: Number(Number(newValue).toFixed(3)),
				shrink,
				type: "suburbanDistanceOverride",
			});
		},
		[activeOrder, getOutOfTownBaseKm],
	);

	const outOfTownDistanceLabel = useMemo(() => {
		// --- old ---
		// const outOfTownDistance = outOfTownDistanceValue?.value;
		// return isNumber(outOfTownDistance)
		// 	? `${Number(Math.max(outOfTownDistance, 0)).toFixed(3)} ${
		// 			t(`units.km`) ?? "km"
		// 	  }`
		// 	: "-";

		const outOfTownDistance = outOfTownDistanceValue?.value;

		return isNumber(outOfTownDistance)
			? `${Number(Math.max(outOfTownDistance, 0)).toFixed(3)} ${
					t(`units.km`) ?? "km"
			  }`
			: "-";
	}, [outOfTownDistanceValue, t]);

	const distance = useMemo(() => {
		const value =
			activeOrder?.cost?.distances?.total ||
			activeOrder?.route?.distance ||
			0;
		return isNumber(value)
			? `${((value ?? 0) / 1000).toFixed(3)} ${t(`units.km`) ?? "km"}`
			: "-";
	}, [activeOrder?.cost?.distances?.total, activeOrder?.route?.distance, t]);

	const idleTimeValue = useMemo<Value | undefined>(() => {
		let idleTimeMilliseconds = 0;
		let withClients = 0;
		if (!activeOrder?.id) return undefined;
		if (isNumber(activeOrder?.additionalFields?.idleTimeMilliseconds)) {
			idleTimeMilliseconds =
				activeOrder.additionalFields.idleTimeMilliseconds;
		}
		if (isNumber(activeOrder?.downtimes?.withClients)) {
			withClients = activeOrder?.downtimes?.withClients;
		}

		const mainTime = Math.max(withClients, idleTimeMilliseconds);
		const value = Math.max(mainTime, 0);

		// const value = Math.max(withClients + idleTimeMilliseconds, 0); // old

		return {
			action: "add",
			value: value / 60e3,
		};
	}, [
		activeOrder?.additionalFields?.idleTimeMilliseconds,
		activeOrder?.downtimes?.withClients,
		activeOrder?.id,
	]);

	const idleTimeLabel = useMemo(() => {
		const value = idleTimeValue?.value ? idleTimeValue.value * 60e3 : 0;

		return isNumber(idleTimeValue?.value)
			? `${dateFns.formatTime(value, {
					timezone: "UTC",
					isTimezone: true,
					formatTime: ConstantFormat.HMS_UK,
			  })} ${t(`units.minute`) ?? "min"}`
			: "-";
	}, [dateFns, idleTimeValue?.value, t]);

	const updateIdleTime = useCallback(
		(newIdleTime: Value) => {
			if (!("id" in activeOrder)) return;
			// let withClients = 0;

			// if (isNumber(activeOrder?.downtimes?.withClients)) {
			// 	withClients = activeOrder?.downtimes?.withClients;
			// }

			const baseIdleTimeValue = idleTimeValue?.value ?? 0;

			const newIdleTimeValue =
				newIdleTime.action === "add"
					? baseIdleTimeValue + newIdleTime.value
					: newIdleTime.value;

			const idleTimeMilliseconds = newIdleTimeValue * 60e3;

			// const newIdleTimeValue =
			// 	newIdleTime.action === "add"
			// 		? baseIdleTimeValue + newIdleTime.value
			// 		: newIdleTime.value;

			// const idleTimeMilliseconds = newIdleTimeValue * 60e3 - withClients;

			Order.updateAdditionalFieldsByOrderId({
				orderId: activeOrder.id,
				value: idleTimeMilliseconds,
				type: "idleTimeMilliseconds",
			});
		},
		[activeOrder, idleTimeValue?.value],
	);

	const currencyLabel = useMemo(
		() => currencyGlobalSettings,
		[currencyGlobalSettings],
	);

	const stringifyPrice = useCallback(
		(price: number | undefined) => {
			if (isUndefined(price)) {
				return "-";
			}

			let processedPrice = price;
			let precision = 2;

			if (additionalTariffSettings) {
				const { rounding } =
					additionalTariffSettings.additionalFields.general;

				precision = rounding.precision;
				const multiplier = 10 ** precision;
				if (rounding.multiple.active) {
					processedPrice /= rounding.multiple.value;
				} else {
					processedPrice *= 10 ** precision;
				}

				switch (rounding.method) {
					case RoundingMethod.GENERAL:
						processedPrice =
							Math.round(processedPrice * multiplier) /
							multiplier;
						break;
					case RoundingMethod.CEIL:
						processedPrice =
							Math.ceil(processedPrice * multiplier) / multiplier;
						break;
					case RoundingMethod.FLOOR:
						processedPrice =
							Math.floor(processedPrice * multiplier) /
							multiplier;
						break;
					default:
						break;
				}

				if (rounding.multiple.active) {
					processedPrice *= rounding.multiple.value;
				} else {
					processedPrice /= 10 ** precision;
				}
			}

			return `${processedPrice.toFixed(
				precision,
			)} ${currencyLabel}`.trim();
		},
		[additionalTariffSettings, currencyLabel],
	);

	const additionalPriceValue = useMemo<Value | undefined>(
		() =>
			isNumber(activeOrder?.additionalFields?.additionalCost)
				? {
						action: "add",
						value: activeOrder.additionalFields
							.additionalCost as number,
				  }
				: undefined,
		[activeOrder?.additionalFields?.additionalCost],
	);

	const additionalPriceLabel = useMemo(
		() => stringifyPrice(additionalPriceValue?.value),
		[additionalPriceValue, stringifyPrice],
	);

	const discountLabel = useMemo(() => {
		if (!activeOrder?.cost?.discount) return "";
		const bonusAmount = activeOrder?.cost?.discount?.bonusAmount || 0;
		const fixAmount = activeOrder?.cost?.discount?.fixedAmount || 0;
		const val = bonusAmount + fixAmount;
		if (!val) return "";
		if (!currencyLabel) return "";
		const precision =
			additionalTariffSettings?.additionalFields?.general.rounding
				.precision ?? 2;

		const multiplier = 10 ** precision;
		const discountPrice = Math.round(val * multiplier) / multiplier;
		return `${discountPrice} ${currencyLabel}`;
	}, [
		activeOrder?.cost?.discount,
		additionalTariffSettings?.additionalFields?.general?.rounding
			?.precision,
		currencyLabel,
	]);

	const updateAdditionalCost = useCallback(
		async (newAdditionalCost: Value) => {
			if (!("id" in activeOrder)) return;

			const newAdditionalFields = cloneDeep(activeOrder.additionalFields);
			const activeOrderCost = cloneDeep(activeOrder.cost);

			const additionalCost = newAdditionalFields?.additionalCost ?? 0;
			const watchedPrice = activeOrderCost.value ?? 0;
			const dataValue = newAdditionalCost?.value ?? 0;
			const cost = dataValue + Number(additionalCost);
			const prevPrice = Number(watchedPrice) - Number(additionalCost);
			const payload = Number(additionalCost) + dataValue;

			const valueAdd =
				cost + prevPrice <= 0 ? -Number(prevPrice) : payload;
			const valueSet =
				cost + prevPrice < 0 ? -Number(prevPrice) : dataValue;

			const newAdditionalPriceValue =
				newAdditionalCost.action === "add" ? valueAdd : valueSet;

			await Order.updateAdditionalFieldsByOrderId({
				orderId: activeOrder.id,
				value: newAdditionalPriceValue,
				type: "additionalCost",
			});

			await refreshActiveOrder(activeOrder.id);
		},
		[activeOrder, refreshActiveOrder],
	);

	const updateAdditionalPrice = useCallback(
		async (newAdditionalPrice: Value) => {
			if (!("id" in activeOrder)) return;

			const newAdditionalFields = cloneDeep(activeOrder.additionalFields);
			const activeOrderCost = cloneDeep(activeOrder.cost);

			const additionalCost = newAdditionalFields?.additionalCost ?? 0;
			const watchedPrice = activeOrderCost.value ?? 0;
			const dataValue = newAdditionalPrice?.value ?? 0;
			const cost = dataValue + Number(additionalCost);
			const prevPrice = Number(watchedPrice) - Number(additionalCost);
			const dataValuePrice =
				dataValue - Number(watchedPrice) + Number(additionalCost);
			const payload = Number(additionalCost) + dataValue;

			const valueAdd2 =
				prevPrice + payload >= 0 ? Number(payload) : payload;

			const valueAdd =
				cost + prevPrice <= 0 ? -Number(prevPrice) : valueAdd2;

			const valueSet2 =
				cost + prevPrice <= 0
					? -Number(prevPrice)
					: -(prevPrice - dataValue);

			const valueSet = dataValue >= 0 ? dataValuePrice : valueSet2;

			const newAdditionalPriceValue =
				newAdditionalPrice.action === "add" ? valueAdd : valueSet;

			await Order.updateAdditionalFieldsByOrderId({
				orderId: activeOrder.id,
				value: newAdditionalPriceValue,
				type: "additionalCost",
			});

			await refreshActiveOrder(activeOrder.id);
		},
		[activeOrder, refreshActiveOrder],
	);

	const priceValue = useMemo<Value | undefined>(() => {
		if (
			!isNumber(activeOrder?.cost?.value) &&
			!isNumber(activeOrder?.additionalFields?.additionalCost)
		) {
			return undefined;
		}

		const total = activeOrder?.cost?.value ?? 0;

		return {
			action: "set",
			value: total,
		};
	}, [
		activeOrder?.additionalFields?.additionalCost,
		activeOrder?.cost?.value,
	]);

	const priceLabel = useMemo(
		() => stringifyPrice(priceValue?.value),
		[priceValue, stringifyPrice],
	);

	const updatePrice = useCallback(
		(newPrice: Value) => {
			if (!("id" in activeOrder)) return;

			updateAdditionalPrice(newPrice);
		},
		[activeOrder, updateAdditionalPrice],
	);

	const awaitingTimeValue = useMemo<Value | undefined>(() => {
		if (!activeOrder?.id) return undefined;
		let waitingTimeMilliseconds = 0;
		let waitingClient = 0;
		if (isNumber(activeOrder?.additionalFields?.waitingTimeMilliseconds)) {
			waitingTimeMilliseconds =
				activeOrder.additionalFields.waitingTimeMilliseconds;
		}
		if (isNumber(activeOrder?.downtimes?.waitingClient)) {
			waitingClient = activeOrder?.downtimes?.waitingClient;
		}
		const value = Math.max(waitingClient + waitingTimeMilliseconds, 0);

		return {
			action: "add",
			value: value / 60e3,
		};
	}, [
		activeOrder?.additionalFields?.waitingTimeMilliseconds,
		activeOrder?.downtimes?.waitingClient,
		activeOrder?.id,
	]);

	const awaitingTimeLabel = useMemo(() => {
		const value = awaitingTimeValue?.value
			? awaitingTimeValue.value * 60e3
			: 0;

		return isNumber(awaitingTimeValue?.value)
			? `${dateFns.formatTime(value, {
					timezone: "UTC",
					isTimezone: true,
					formatTime: ConstantFormat.HMS_UK,
			  })} ${t(`units.minute`) ?? "min"}`
			: "-";
	}, [awaitingTimeValue?.value, dateFns, t]);

	const updateAwaitingTime = useCallback(
		(newAwaitingTime: Value) => {
			if (!("id" in activeOrder)) return;

			let waitingClient = 0;

			if (isNumber(activeOrder?.downtimes?.waitingClient)) {
				waitingClient = activeOrder?.downtimes?.waitingClient;
			}

			const newAwaitingTimeValue =
				newAwaitingTime.action === "add"
					? (awaitingTimeValue?.value ?? 0) + newAwaitingTime.value
					: newAwaitingTime.value;

			const waitingTimeMilliseconds =
				newAwaitingTimeValue * 60e3 - waitingClient;

			Order.updateAdditionalFieldsByOrderId({
				orderId: activeOrder.id,
				value: waitingTimeMilliseconds,
				type: "waitingTimeMilliseconds",
			});
		},
		[activeOrder, awaitingTimeValue?.value],
	);

	const hourlyTimeValue = useMemo<Value | undefined>(
		() =>
			isNumber(activeOrder?.additionalFields?.hourlyMilliseconds)
				? {
						action: "add",
						value: (activeOrder.additionalFields
							.hourlyMilliseconds / 60e3) as number,
				  }
				: undefined,
		[activeOrder?.additionalFields?.hourlyMilliseconds],
	);

	const hourlyTimeLabel = useMemo(() => {
		const hourlyTime = hourlyTimeValue?.value;

		return isNumber(hourlyTime)
			? `${formatDuration(hourlyTime)} ${t(`units.minute`) ?? "min"}`
			: "-";
	}, [hourlyTimeValue, t]);

	const updateHourlyTime = useCallback(
		(newHourlyTime: Value) => {
			if (!("id" in activeOrder)) return;

			const newHourlyTimeValue =
				newHourlyTime.action === "add"
					? (hourlyTimeValue?.value ?? 0) + newHourlyTime.value
					: newHourlyTime.value;

			const hourlyMilliseconds = newHourlyTimeValue * 60e3;

			Order.updateAdditionalFieldsByOrderId({
				orderId: activeOrder.id,
				value: hourlyMilliseconds,
				type: "hourlyMilliseconds",
			});
		},
		[activeOrder, hourlyTimeValue?.value],
	);

	return {
		distance,

		outOfTownDistanceValue,
		outOfTownDistanceLabel,
		updateOutOfTownDistance,

		idleTimeValue,
		idleTimeLabel,
		updateIdleTime,

		priceValue,
		priceLabel,
		updatePrice,

		discountLabel,

		additionalPriceValue,
		additionalPriceLabel,
		updateAdditionalPrice,
		updateAdditionalCost,

		awaitingTimeValue,
		awaitingTimeLabel,
		updateAwaitingTime,

		hourlyTimeValue,
		hourlyTimeLabel,
		updateHourlyTime,
	};
}

export default useBottomData;
